import { apiFetch } from '../auth/apiFetch.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Bringup script authoring (PRD-0009 Phase B). The operator edits the script
// text in the console; the backend hashes it, stores it to S3 server-side, and
// records the reference on the revision.

export interface BringupContent {
  content: string;
  script_sha256: string | null;
}

async function readJson(r: Response): Promise<unknown> {
  if (!r.ok) {
    const detail = (await r.json().catch(() => null)) as { detail?: unknown } | null;
    const msg =
      typeof detail?.detail === 'string' ? detail.detail : `status ${r.status}`;
    throw new Error(msg);
  }
  return r.json();
}

function contentPath(morphology: string, version: string): string {
  return (
    `/api/morphologies/${encodeURIComponent(morphology)}` +
    `/versions/${encodeURIComponent(version)}/script/content`
  );
}

export function useBringupContent(
  morphology: string | undefined,
  version: string | undefined,
) {
  return useQuery({
    queryKey: ['bringup-content', morphology, version] as const,
    enabled: !!morphology && !!version,
    queryFn: async (): Promise<BringupContent> => {
      const body = (await readJson(
        await apiFetch(contentPath(morphology as string, version as string)),
      )) as { content?: string; script_sha256?: string | null };
      return { content: body.content ?? '', script_sha256: body.script_sha256 ?? null };
    },
  });
}

export function useSaveBringupContent(morphology: string, version: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string): Promise<unknown> =>
      apiFetch(contentPath(morphology, version), {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ content }),
      }).then(readJson),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['bringup-content', morphology, version],
      });
      // bringup_ready on the versions list may flip.
      void qc.invalidateQueries({ queryKey: ['catalog-versions', morphology] });
    },
  });
}

// The revision's bringup PAYLOAD digest (PRD-0009). Neither upload NOR the digest is
// entered here.
//
// The payload tarball -- vendor data plus out-of-tree driver source -- is built and
// uploaded by the robot repo's CI, which writes it straight to
// bringup/{tenant}/{morphology}/{version}/payload.tar.gz. Having a human move a file
// between buckets is a step that gets skipped, and CI already has the bytes.
//
// So the console's only job is recording the digest CI printed. That is enough on its
// own: the API derives the S3 key server-side from (tenant, morphology, version), so
// PUT .../payload takes a sha and nothing else. The digest is the load-bearing half --
// the agent verifies it before unpacking driver source it is about to build and install
// as root.
function payloadPath(morphology: string, version: string): string {
  return (
    `/api/morphologies/${encodeURIComponent(morphology)}` +
    `/versions/${encodeURIComponent(version)}/payload`
  );
}

// SENDS NO DIGEST. The server hashes the stored object and records that
// (hiwonder_ros2#96 decision 6), so nobody retypes the one value that decides whether a
// robot will unpack and run an archive as root. A 404 here means CI has not published a
// payload for this revision yet.
export function useSyncBringupPayload(morphology: string, version: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<unknown> =>
      apiFetch(payloadPath(morphology, version), {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      }).then(readJson),
    // The versions list carries payload_key/payload_sha256, so it is what shows the
    // recorded value back.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalog-versions', morphology] });
    },
  });
}
